import { useCallback, useEffect, useReducer, useRef } from "react";

import { errorText } from "@/shared/lib/apiErrors";

import { flowService, type FlowJob } from "../../api/flowService";
import { flowReducer, IDLE, isTerminal, type FlowEvent } from "../../model/generateFlow";

/** 轮询间隔(契约:2s)。 */
export const POLL_MS = 2000;
/** 测试用:可改的轮询间隔(组件测把它调成 0,不等真 2 秒)。 */
export const flowPoll = { ms: POLL_MS };
/** 轮询连续失败这么多次才判失败(一次网络抖动不该把整站生成判死)。 */
const POLL_MAX_ERRORS = 3;

/**
 * 生成 / 更新官网 / 单张重画这类异步任务(FlowJob)的前端状态:`flowReducer` + 轮询 `GET flow/jobs/{jobId}`。
 * ★ 按站点 id 防串台:换站 / 卸载即停轮询,晚到的回包丢掉。
 * ★ `resume`:进页面先问一次本站最近任务,在跑就接着轮询(用户刷新页面不丢进度)。
 */
export function useFlowJob(
  siteId: string,
  opts: { pollMs?: number; resume?: boolean; onSettled?: (task: FlowJob) => void } = {}
) {
  const { pollMs = flowPoll.ms, resume = false } = opts;
  const [phase, dispatch] = useReducer(flowReducer, IDLE);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gen = useRef(0);
  const onSettled = useRef(opts.onSettled);
  onSettled.current = opts.onSettled;

  const stop = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const track = useCallback(
    (task: FlowJob) => {
      const my = gen.current;
      dispatch({ type: "PROGRESS", task });
      if (isTerminal(task)) {
        onSettled.current?.(task);
        return;
      }
      let errors = 0;
      const tick = (taskId: string) => {
        timer.current = setTimeout(async () => {
          try {
            const t = await flowService.job(siteId, taskId);
            if (gen.current !== my) return;
            errors = 0;
            dispatch({ type: "PROGRESS", task: t });
            if (isTerminal(t)) onSettled.current?.(t);
            else tick(taskId);
          } catch (e) {
            if (gen.current !== my) return;
            errors += 1;
            if (errors >= POLL_MAX_ERRORS) dispatch({ type: "FAIL", error: errorText(e) });
            else tick(taskId);
          }
        }, pollMs);
      };
      tick(task.jobId);
    },
    [siteId, pollMs]
  );

  /** 开始一个任务:`start` 发写口拿到任务,然后轮询到终态。 */
  const run = useCallback(
    async (start: () => Promise<FlowJob>) => {
      stop();
      gen.current += 1;
      const my = gen.current;
      dispatch({ type: "START" });
      try {
        const t = await start();
        if (gen.current !== my) return;
        track(t);
      } catch (e) {
        if (gen.current !== my) return;
        dispatch({ type: "FAIL", error: errorText(e) });
      }
    },
    [stop, track]
  );

  useEffect(() => {
    gen.current += 1;
    const my = gen.current;
    if (resume && siteId) {
      flowService
        .latestJob(siteId)
        .then((t) => {
          if (gen.current === my && t && !isTerminal(t)) {
            dispatch({ type: "START" });
            track(t);
          }
        })
        .catch(() => {
          /* 没有在跑的任务 / 老后端:照常填表 */
        });
    }
    return () => {
      gen.current += 1;
      stop();
    };
  }, [siteId, resume, stop, track]);

  const send = useCallback((ev: FlowEvent) => dispatch(ev), []);

  return { phase, send, run };
}
